import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ZoomIn } from 'lucide-react';
import { FadeUp } from '../../../components/shared/Motion';
import { EditableText, EditableImage, HrefPill } from '../../components';

function ActivityGallery({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const items = data.items || [];
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  const [lightbox, setLightbox] = useState(null);

  return (
    <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="container-page">
        <FadeUp className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow justify-center"><span className="h-px w-8 bg-olive" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="section-title mt-4">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
            <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />
          </h2>
          <p className="section-lead mx-auto mt-4">
            <EditableText value={data.subheading} onChange={(t) => update({ subheading: t })} as="span" />
          </p>
        </FadeUp>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((g, i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <button
                type="button"
                onClick={() => setLightbox(i)}
                className="group relative block w-full overflow-hidden rounded-xl shadow-soft hover:shadow-glow-olive transition-all duration-500"
              >
                <EditableImage
                  src={g.image?.src}
                  alt={g.image?.alt || g.label}
                  onChange={(src, alt) => updateItem(i, { image: { src, alt } })}
                  className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-[1.08]"
                />
                <div className="absolute inset-0 bg-olive-deep/0 group-hover:bg-olive-deep/55 transition-colors duration-500" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent text-ink shadow-glow scale-75 group-hover:scale-100 transition-transform duration-500">
                    <ZoomIn size={20} />
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-olive-deep text-[11px] font-bold uppercase tracking-[0.16em] shadow">
                    <EditableText value={g.label} onChange={(t) => updateItem(i, { label: t })} as="span" />
                  </span>
                  <span className="text-[10px] font-mono text-white/85 bg-black/30 backdrop-blur rounded-full px-2 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {String(i + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
                  </span>
                </div>
              </button>
            </FadeUp>
          ))}
        </div>

        {data.cta && (
          <FadeUp delay={0.5}>
            <div className="mt-12 flex flex-col items-center">
              <Link to={data.cta.href || '#'} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent text-ink font-bold text-sm uppercase tracking-[0.16em] shadow-[0_14px_36px_-10px_rgba(197,120,99,0.7)] hover:shadow-[0_20px_50px_-10px_rgba(197,120,99,1)] hover:-translate-y-0.5 transition-all duration-300">
                <EditableText value={data.cta.label} onChange={(t) => update({ cta: { ...(data.cta || {}), label: t } })} as="span" />
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <HrefPill value={data.cta.href} onChange={(h) => update({ cta: { ...(data.cta || {}), href: h } })} />
            </div>
          </FadeUp>
        )}
      </div>

      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="fixed inset-0 z-[70] bg-olive-footer/92 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
            <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} className="absolute top-6 right-6 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-ink text-white transition" aria-label="Close">
              <ArrowUpRight size={20} style={{ transform: 'rotate(45deg)' }} />
            </button>
            <motion.div key={lightbox} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }} onClick={(e) => e.stopPropagation()} className="relative max-w-5xl w-full">
              <img src={items[lightbox].image?.src} alt={items[lightbox].label} className="w-full max-h-[80vh] object-contain rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]" />
              <div className="mt-4 flex items-center justify-between">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-ink font-bold text-xs uppercase tracking-[0.18em]">
                  {items[lightbox].label}
                </span>
                <div className="flex items-center gap-2 text-white/70 text-xs font-mono">
                  <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + items.length) % items.length); }} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-accent hover:text-ink transition">← Prev</button>
                  <span>{String(lightbox + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % items.length); }} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-accent hover:text-ink transition">Next →</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default ActivityGallery;
